import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { Attachment, AttachmentInfo, AttachmentPreview, Attachments } from '../Attachments';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { antiFlashWhite } from '../../../styles/colors';
import { useAttachmentQuery } from '../../../../queries/Attachments';

type AIChatContentLike = { _type?: string };

export type AIConversationMessageAttachmentsProps = {
	contents?: AIChatContentLike[];
	impersonationContext?: Api.IImpersonationContext;
};

/**
 * Renders the AIFileContent parts of a chat message as a grid of Attachments.
 * Returns null when the message has no AIFileContent parts.
 */
export const AIConversationMessageAttachments = ({
	contents,
	impersonationContext,
}: AIConversationMessageAttachmentsProps) => {
	const fileContents = (contents ?? [])
		.filter(c => c._type === 'AIFileContent')
		.map(c => c as Api.AIFileContent)
		.filter(c => !!c.fileAttachmentId);
	if (fileContents.length === 0) {
		return null;
	}
	return (
		<Attachments variant='list'>
			{fileContents.map(c => (
				<AIConversationMessageAttachment
					key={c.fileAttachmentId}
					fileAttachmentId={c.fileAttachmentId}
					impersonationContext={impersonationContext}
				/>
			))}
		</Attachments>
	);
};

type AIConversationMessageAttachmentProps = {
	fileAttachmentId: string;
	impersonationContext?: Api.IImpersonationContext;
};

const AIConversationMessageAttachment = ({
	fileAttachmentId,
	impersonationContext,
}: AIConversationMessageAttachmentProps) => {
	const query = useAttachmentQuery({ id: fileAttachmentId, impersonationContext });
	if (query.isLoading || !query.data) {
		return (
			<div className={css(s.placeholderTile)}>
				<LoadingSpinner type='tiny' />
			</div>
		);
	}
	const data = {
		id: fileAttachmentId,
		type: 'file' as const,
		filename: query.data.fileName,
		mediaType: query.data.mimeType ?? '',
		url: query.data.url ?? '',
	};
	return (
		<Attachment data={data}>
			<AttachmentPreview />
			<AttachmentInfo showMediaType />
		</Attachment>
	);
};

const s = StyleSheet.create({
	placeholderTile: {
		boxSizing: 'border-box',
		display: 'flex',
		width: 96,
		height: 96,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		backgroundColor: antiFlashWhite,
	},
});
